"use client";
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { REST_API_URL } from "../api/config";
import { buildHeaders } from "../api/buildHeaders";
import { useTranslation } from "react-i18next";

const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export default function ClientDropdown({ value, clientId, onChange, error }) {
  const [search, setSearch] = useState(typeof value === "string" ? value : value?.name || "");
  const [clients, setClients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const { t } = useTranslation();
  const dropdownRef = useRef(null);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken")
      : null;

  useEffect(() => {
    if (typeof window !== "undefined") {
      const timeZone = getUserTimeZone();
      setUserTimeZone(timeZone);
    }
  }, []);

  const fetchClients = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const headers = buildHeaders();
      const res = await axios.get(`${API_BASE}/client/clients-list/`, {
        headers,
      });

      if (Array.isArray(res.data)) {
        const formattedClients = res.data.map((client) => ({
          ...client,
          formatted_created_at: client.created_at
            ? formatDateWithTimeZone(client.created_at)
            : null,
          formatted_last_visit: client.last_visit
            ? formatDateWithTimeZone(client.last_visit)
            : null,
        }));

        setClients(formattedClients);
        setFiltered(formattedClients);
      }
    } catch (err) {
      console.error("❌ Failed to fetch clients:", err);
      toast.error(t("home.failedLoadClients", { defaultValue: "Failed to load clients" }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    if (!value && !clientId) {
      setSearch("");
    } else if (typeof value === "string") {
      setSearch(value);
    }
  }, [value, clientId]);

  const formatDateWithTimeZone = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        timeZone: userTimeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        timeZone: userTimeZone,
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearch(term);
    onChange(term, null);

    if (!term.trim()) {
      setFiltered(clients);
      return;
    }

    const lower = term.toLowerCase();
    const filteredList = clients.filter((c) =>
      (c.name || "").toLowerCase().includes(lower),
    );
    setFiltered(filteredList);
  };

  const handleSelectClient = (c) => {
    onChange(c.name, c.id);
    setSearch(c.name);
    setOpen(false);
  };

  const handleClear = () => {
    onChange("", null);
    setSearch("");
    setFiltered(clients);
    setOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Determine if a registered client is currently selected
  const selectedClient = clientId
    ? clients.find((c) => c.id === clientId) || {
        id: clientId,
        name: typeof value === "string" ? value : value?.name || search,
      }
    : null;

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {selectedClient ? (
        <div className="w-full flex items-center justify-between px-3.5 py-2 min-h-[44px] bg-gray-50 border border-gray-200 rounded-xl hover:border-gray-300 transition-all">
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <img
              src={
                selectedClient.image_url ||
                "https://cdn-icons-png.flaticon.com/512/149/149071.png"
              }
              alt=""
              className="w-7 h-7 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                e.currentTarget.src =
                  "https://cdn-icons-png.flaticon.com/512/149/149071.png";
              }}
            />
            <div className="flex flex-col min-w-0">
              <span className="font-semibold text-sm text-gray-900 truncate">
                {selectedClient.name}
              </span>
              {selectedClient.email && (
                <span className="text-[11px] text-gray-500 truncate">
                  {selectedClient.email}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors ml-2 flex-shrink-0"
            title={t("cancel", "Clear")}
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="appointment-page calendar-input">
          <input
            type="text"
            placeholder={t("home.placeholderClientName", {
              defaultValue: "Client name",
            })}
            value={search}
            onChange={handleSearch}
            onFocus={() => {
              if (clients.length === 0) fetchClients();
              setOpen(true);
            }}
            className={`w-full border rounded-xl px-3 py-2 outline-none ${
              error ? "border-red-500" : "border-gray-200"
            }`}
          />
        </div>
      )}

      <AnimatePresence>
        {open && !selectedClient && filtered.length > 0 && (
          <motion.ul
            className="absolute top-full left-0 w-full bg-white rounded-xl shadow-lg z-20 overflow-hidden mt-1 border border-gray-100 max-h-60 overflow-y-auto"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
          >
            {loading ? (
              <li className="p-3 text-center text-gray-500 text-sm">
                {t("loading", { defaultValue: "Loading..." })}
              </li>
            ) : (
              filtered.map((c) => (
                <li
                  key={c.id}
                  onClick={() => handleSelectClient(c)}
                  className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-800"
                >
                  <img
                    src={
                      c.image_url ||
                      "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                    }
                    alt={c.name}
                    className="w-8 h-8 rounded-full mr-3 object-cover flex-shrink-0"
                    onError={(e) => {
                      e.currentTarget.src =
                        "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                    }}
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="font-medium text-sm truncate">{c.name}</span>
                    {c.email && (
                      <span className="text-xs text-gray-500 truncate">
                        {c.email}
                      </span>
                    )}
                    {c.last_visit && (
                      <span className="text-xs text-gray-400 mt-0.5">
                        Last visit: {formatDateOnly(c.last_visit)}
                      </span>
                    )}
                  </div>
                </li>
              ))
            )}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}